import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Alert,
  Divider,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  CalendarMonth as CalendarIcon,
  Apartment as BuildingIcon,
  StickyNote2 as NoteIcon,
} from '@mui/icons-material';
import { createIncident, getDefaultIncidentName } from '../Services/IncidentService';
import { CreateIncidentResponseData } from '../Types/Incident';
import { getLocations } from '../Services/LocationService';
import { Location } from '../Types/Location';
import IncidentTypeSelector, { INCIDENT_TYPES } from '../Components/Incidents/IncidentTypeSelector';
import { appColors } from '../Config/Theme';
import AppBreadcrumbs from '../Components/Navigation/AppBreadcrumbs';
import CreateIncidentProgressDialog, {
  CreateIncidentProgressState,
} from '../Components/Incidents/CreateIncidentProgressDialog';
import { useSecurity } from '../Contexts/SecurityProvider';
import RequiredStepSectionHeader from '../Components/Elements/RequiredStepSectionHeader';

import { IncidentType } from '../Types/enums';

const MAX_NOTES = 250;

const generateDefaultName = (typeValue: string): string => {
  const typeLabel = INCIDENT_TYPES.find((t) => t.value === typeValue)?.label ?? 'Incident';
  const now = new Date();
  const date = now.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
  const time = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${typeLabel} \u2013 ${date} ${time}`;
};

const formatLocationLabel = (loc: Location): string => {
  const parts = [loc.locationName];
  if (loc.city && loc.stateProvince) {
    parts.push(`${loc.city}, ${loc.stateProvince}`);
  } else if (loc.stateProvince) {
    parts.push(loc.stateProvince);
  }
  const main = parts.join(' \u2014 ');
  return loc.locationCode ? `${main} (${loc.locationCode})` : main;
};

const filterAndSortLocations = (raw: Location[]): Location[] =>
  raw
    .filter((l) => l.isActive && l.country === 'United States')
    .sort((a, b) => a.locationName.localeCompare(b.locationName) || (a.stateProvince ?? '').localeCompare(b.stateProvince ?? ''));

const CreateIncidentPage: React.FC = () => {
  const navigate = useNavigate();
  const { getDefaultIncidentLocationCandidate, isSecurityLoading } = useSecurity();

  const [incidentType, setIncidentType] = useState<IncidentType | null>(null);
  const [incidentName, setIncidentName] = useState('');
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const hasUserSelectedLocation = useRef(false);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nameError, setNameError] = useState(false);
  const nameManuallyEdited = useRef(false);
  const nameSuggestionGeneration = useRef(0);
  const [nameSuggestionLoading, setNameSuggestionLoading] = useState(false);
  const [nameSuggestionError, setNameSuggestionError] = useState<string | null>(null);
  const [nameSuggestionAttempt, setNameSuggestionAttempt] = useState(0);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // ── Progress dialog state ──
  const [progressState, setProgressState] = useState<CreateIncidentProgressState>('idle');
  const [createdIncident, setCreatedIncident] = useState<CreateIncidentResponseData | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const progressTimers = useRef<ReturnType<typeof setTimeout>[]>([]);

  // ── Locations ──
  const [locations, setLocations] = useState<Location[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(true);
  const [locationsFallback, setLocationsFallback] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLocationsLoading(true);
      const result = await getLocations();
      if (!cancelled) {
        const sorted = filterAndSortLocations(result.locations);
        setLocations(sorted);
        setLocationsFallback(result.isFallback);
        setLocationsLoading(false);

        // Auto-select default location from security metadata if user hasn't chosen one
        if (!hasUserSelectedLocation.current && !selectedLocation) {
          const candidate = getDefaultIncidentLocationCandidate();
          if (candidate) {
            const match =
              (candidate.locationId && sorted.find((l) => l.locationId === candidate.locationId)) ||
              (candidate.locationName && sorted.find((l) => l.locationName.toLowerCase() === candidate.locationName!.toLowerCase()));
            if (match) setSelectedLocation(match);
          }
        }
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-select default location once security data arrives (if locations already loaded)
  useEffect(() => {
    if (!isSecurityLoading && !hasUserSelectedLocation.current && !selectedLocation && locations.length > 0) {
      const candidate = getDefaultIncidentLocationCandidate();
      if (candidate) {
        const match =
          (candidate.locationId && locations.find((l) => l.locationId === candidate.locationId)) ||
          (candidate.locationName && locations.find((l) => l.locationName.toLowerCase() === candidate.locationName!.toLowerCase()));
        if (match) setSelectedLocation(match);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSecurityLoading]);

  useEffect(() => {
    if (!selectedLocation || incidentType || nameManuallyEdited.current) return;
    const generation = ++nameSuggestionGeneration.current;
    let cancelled = false;
    setIncidentName('');
    setNameSuggestionLoading(true);
    setNameSuggestionError(null);
    const isCurrent = () => !cancelled && generation === nameSuggestionGeneration.current && !nameManuallyEdited.current;
    getDefaultIncidentName(selectedLocation.locationId)
      .then((suggestion) => {
        if (isCurrent()) setIncidentName(suggestion.incidentName);
      })
      .catch(() => {
        if (isCurrent()) setNameSuggestionError('The default name is unavailable. Retry, enter a name, or start the incident to have a name assigned.');
      })
      .finally(() => { if (isCurrent()) setNameSuggestionLoading(false); });
    return () => { cancelled = true; };
  }, [selectedLocation, incidentType, nameSuggestionAttempt]);

  const handleTypeChange = useCallback((value: string) => {
    const newType = value ? value as IncidentType : null;
    nameSuggestionGeneration.current++;
    setIncidentType(newType);
    setNameSuggestionLoading(false);
    setNameSuggestionError(null);
    if (!nameManuallyEdited.current) {
      setIncidentName(newType ? generateDefaultName(newType) : '');
    }
  }, []);

  const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setIncidentName(e.target.value);
    setNameError(false);
    nameManuallyEdited.current = true;
    nameSuggestionGeneration.current++;
    setNameSuggestionLoading(false);
    setNameSuggestionError(null);
  }, []);

  const usesDefaultName = !incidentType && !nameManuallyEdited.current;
  const canStart = !submitting && Boolean(selectedLocation) && (Boolean(incidentName.trim()) || usesDefaultName);

  // Clean up progress timers on unmount
  useEffect(() => {
    return () => { progressTimers.current.forEach(clearTimeout); };
  }, []);

  const clearProgressTimers = () => {
    progressTimers.current.forEach(clearTimeout);
    progressTimers.current = [];
  };

  const handleSubmit = useCallback(async () => {
    setSubmitAttempted(true);
    const trimmedName = incidentName.trim();
    if (!selectedLocation || submitting) return;
    const useDefaultName = !incidentType && !nameManuallyEdited.current;
    if (!trimmedName && !useDefaultName) {
      setNameError(true);
      return;
    }
    setNameError(false);
    setError(null);
    setCreateError(null);
    setCreatedIncident(null);
    setSubmitting(true);
    setProgressState('creating');
    nameSuggestionGeneration.current++;
    setNameSuggestionLoading(false);

    // Timed transitions for visual progress
    clearProgressTimers();
    progressTimers.current.push(
      setTimeout(() => setProgressState((prev) => prev === 'creating' ? 'loadingRoster' : prev), 700),
      setTimeout(() => setProgressState((prev) => prev === 'loadingRoster' || prev === 'creating' ? 'finalizing' : prev), 1500),
    );

    try {
      const result = await createIncident({
        incidentName: trimmedName,
        incidentType,
        useDefaultName,
        location: selectedLocation.locationId,
        notes: notes.trim() || undefined,
        requestedViews: null,
      });
      clearProgressTimers();
      setCreatedIncident(result);
      setIncidentName(result.incidentName);
      setProgressState('success');
    } catch (err: unknown) {
      clearProgressTimers();
      const msg = err instanceof Error ? err.message : 'Something went wrong while creating the incident. Please try again.';
      setCreateError(msg);
      setError(msg);
      setProgressState('error');
    } finally {
      setSubmitting(false);
    }
  }, [incidentName, incidentType, selectedLocation, notes, submitting]);

  const handleProgressOk = useCallback(() => {
    if (!createdIncident) return;
    setProgressState('idle');
    navigate(`/incidents/${createdIncident.incidentId}/views`);
  }, [createdIncident, navigate]);

  const handleProgressClose = useCallback(() => {
    setProgressState('idle');
  }, []);

  const handleProgressRetry = useCallback(() => {
    setProgressState('idle');
    // Small delay so dialog closes before re-opening
    setTimeout(() => { handleSubmit(); }, 100);
  }, [handleSubmit]);

  const handleBack = () => navigate('/');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Scrollable content */}
      <Box sx={{ flex: 1, overflow: 'auto', pt: 1, px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 }, maxWidth: 1200, mx: 'auto', width: '100%' }}>
        {/* Header */}
        <AppBreadcrumbs items={[
          { label: 'Incidents', path: '/' },
          { label: 'New Incident' },
        ]} />
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: appColors.text.heading }}>
              Create Incident
            </Typography>
            <Typography variant="body2" sx={{ color: appColors.text.body, mt: 0.25 }}>
              Start a new evacuation or safety incident.
              {' '}Only one active incident is allowed per location.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<PlayIcon />}
            onClick={handleSubmit}
            disabled={!canStart}
            sx={{
              minHeight: 40,
              fontSize: '0.875rem',
              fontWeight: 700,
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            Start Incident
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Incident Type */}
        <RequiredStepSectionHeader
          stepNumber={1}
          title="Incident Type"
          subtitle="Optional now. Select a type before closing the incident."
          isComplete={Boolean(incidentType)}
        />
        <IncidentTypeSelector value={incidentType ?? ''} onChange={handleTypeChange} />
        {incidentType && <Button onClick={() => handleTypeChange('')} sx={{ mt: 1 }}>Clear incident type</Button>}

        <Divider sx={{ my: 3 }} />

        {/* Incident Details */}
        <RequiredStepSectionHeader
          stepNumber={2}
          title="Incident Details"
          subtitle="Where is this happening and what should it be called?"
          required
          isComplete={Boolean(selectedLocation && (incidentName.trim() || usesDefaultName))}
          hasError={submitAttempted && (!selectedLocation || (!incidentName.trim() && !usesDefaultName))}
        />

        <Box sx={{ display: 'flex', gap: 2, mb: 0.5, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
          {/* Location (first — drives roster generation) */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: appColors.text.heading, mb: 0.5 }}>
              Location <Typography component="span" sx={{ color: appColors.status.error }}>*</Typography>
            </Typography>
            <Autocomplete
              options={locations}
              getOptionLabel={formatLocationLabel}
              value={selectedLocation}
              onChange={(_e, value) => {
                hasUserSelectedLocation.current = true;
                nameSuggestionGeneration.current++;
                setSelectedLocation(value);
                setNameSuggestionLoading(false);
                setNameSuggestionError(null);
                if (!incidentType && !nameManuallyEdited.current) setIncidentName('');
              }}
              disabled={submitting}
              loading={locationsLoading}
              isOptionEqualToValue={(option, value) => option.locationId === value.locationId}
              renderInput={(params) => {
                const { slotProps: paramSlotProps, ...restParams } = params;
                const inputSlotProps = (paramSlotProps?.input ?? {}) as Record<string, any>;
                return (
                  <TextField
                    {...restParams}
                    size="small"
                    placeholder="Select incident location"
                    slotProps={{
                      ...paramSlotProps,
                      input: {
                        ...inputSlotProps,
                        'aria-required': true,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <BuildingIcon sx={{ fontSize: 18, color: appColors.text.placeholder }} />
                            </InputAdornment>
                            {inputSlotProps.startAdornment}
                          </>
                        ),
                        endAdornment: (
                          <>
                            {locationsLoading ? <CircularProgress size={18} /> : null}
                            {inputSlotProps.endAdornment}
                          </>
                        ),
                      },
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', minHeight: 44 } }}
                  />
                );
              }}
              sx={{ width: '100%' }}
              noOptionsText="No locations available"
            />
            {!selectedLocation && !locationsLoading && (
              <Typography variant="caption" sx={{ color: appColors.text.caption, mt: 0.5, display: 'block' }}>
                Location is required to generate the incident roster.
              </Typography>
            )}
            {locationsFallback && !locationsLoading && (
              <Typography variant="caption" sx={{ color: appColors.text.placeholder, mt: 0.5, display: 'block' }}>
                Using fallback location list until the locations API is available.
              </Typography>
            )}
          </Box>

          {/* Incident Name */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: appColors.text.heading, mb: 0.5 }}>
              Incident Name <Typography component="span" sx={{ color: appColors.status.error }}>*</Typography>
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={incidentName}
              onChange={handleNameChange}
              error={nameError}
              helperText={nameError ? 'Incident name is required.'
                : nameSuggestionLoading ? 'Generating the default incident name…'
                : incidentType ? 'Auto-generated based on type and date/time. Edit only if needed.'
                : 'A numbered name and date/time are assigned for this office. Numbering restarts daily. Edit only if needed.'}
              slotProps={{
                htmlInput: { 'aria-label': 'Incident name' },
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarIcon sx={{ fontSize: 18, color: appColors.text.placeholder }} />
                    </InputAdornment>
                  ),
                },
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
            />
            {nameSuggestionError && <Alert severity="warning" sx={{ mt: 1 }} action={
              <Button color="inherit" onClick={() => setNameSuggestionAttempt((attempt) => attempt + 1)}>Retry</Button>
            }>{nameSuggestionError}</Alert>}
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Notes */}
        <Typography variant="body2" sx={{ fontWeight: 600, color: appColors.text.heading, mb: 0.5 }}>
          Notes{' '}
          <Typography component="span" variant="caption" sx={{ color: appColors.text.placeholder }}>(optional)</Typography>
        </Typography>
        <Box sx={{ position: 'relative', mb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              border: `1px solid ${appColors.border.default}`,
              borderRadius: '8px',
              bgcolor: appColors.surface.card,
              minHeight: 80,
              '&:hover': { borderColor: appColors.border.hover },
              '&:focus-within': { borderColor: appColors.primary.main, borderWidth: 2 },
            }}
          >
            <Box sx={{ pl: 1.5, pt: 1.5, flexShrink: 0 }}>
              <NoteIcon sx={{ fontSize: 18, color: appColors.text.placeholder }} />
            </Box>
            <textarea
              placeholder="Add any relevant instructions or notes for responders…"
              value={notes}
              onChange={(e) => { if (e.target.value.length <= MAX_NOTES) setNotes(e.target.value); }}
              rows={2}
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                resize: 'none',
                padding: '12px 12px 20px 8px',
                fontFamily: 'inherit',
                fontSize: '0.875rem',
                lineHeight: 1.5,
                color: appColors.text.heading,
                backgroundColor: 'transparent',
              }}
            />
          </Box>
          <Typography
            variant="caption"
            sx={{ position: 'absolute', bottom: 4, right: 8, color: appColors.text.placeholder }}
          >
            {notes.length} / {MAX_NOTES}
          </Typography>
        </Box>

        {/* Info banner */}
        <Alert severity="info" sx={{ borderRadius: '8px' }}>
          Roster will be generated automatically from the Data Warehouse for the selected location when the incident starts.
        </Alert>

        {/* Pre-start summary */}
        {selectedLocation && (
          <Typography variant="body2" sx={{ mt: 1.5, color: appColors.text.body, fontWeight: 500 }}>
            {incidentType ? `${INCIDENT_TYPES.find((t) => t.value === incidentType)?.label ?? 'Incident'} incident` : 'Type not selected'} &middot; {selectedLocation.locationName} &middot; Roster will be generated automatically
          </Typography>
        )}
      </Box>

      {/* Bottom action bar */}
      <Box
        sx={{
          borderTop: `1px solid ${appColors.border.default}`,
          bgcolor: appColors.surface.card,
          px: { xs: 2, sm: 3 },
          py: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
        }}
      >
        {!submitting && !selectedLocation && (
          <Typography variant="caption" sx={{ color: appColors.text.caption }}>
            Select an incident location to continue.
          </Typography>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, width: '100%' }}>
          <Button
            variant="outlined"
            onClick={handleBack}
            sx={{
              flex: 1,
              maxWidth: 280,
              minHeight: 48,
              fontSize: '0.9375rem',
              color: appColors.text.heading,
              borderColor: appColors.border.subtle,
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<PlayIcon />}
            onClick={handleSubmit}
            disabled={!canStart}
            sx={{
              flex: 1,
              maxWidth: 280,
              minHeight: 48,
              fontSize: '0.9375rem',
              fontWeight: 700,
            }}
          >
            Start Incident
          </Button>
        </Box>
      </Box>

      {/* Progress dialog */}
      <CreateIncidentProgressDialog
        open={progressState !== 'idle'}
        state={progressState}
        errorMessage={createError}
        incidentName={createdIncident?.incidentName ?? (incidentName.trim() || 'Incident')}
        createdIncident={createdIncident}
        onOk={handleProgressOk}
        onClose={handleProgressClose}
        onRetry={handleProgressRetry}
      />
    </Box>
  );
};

export default CreateIncidentPage;
